import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Target,
  BookOpen,
  Sparkles,
  BarChart3,
  GraduationCap,
  TrendingUp,
  FileText,
  Settings,
} from "lucide-react";
import {
  pageTitle,
  microLabelWide,
  accentBarSection,
  bodySecondary,
  motionFadeUp,
  motionPageTransition,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import { getProgram } from "../../../../services/program.service";
import POsTab from "./tabs/POsTab";
import PSOsTab from "./tabs/PSOsTab";
import PEOsTab from "./tabs/PEOsTab";
import TargetsTab from "./tabs/TargetsTab";
import CoursesTab from "./tabs/CoursesTab";
import AttainmentTab from "./tabs/AttainmentTab";
import AtrTab from "./tabs/AtrTab";
import AttainmentSettingsTab from "./tabs/AttainmentSettingsTab";

const TABS = [
  { id: "pos", label: "Program Outcomes", icon: Target },
  { id: "psos", label: "PSOs", icon: Sparkles },
  { id: "peos", label: "PEOs", icon: GraduationCap },
  { id: "targets", label: "Attainment Targets", icon: BarChart3 },
  { id: "courses", label: "Courses", icon: BookOpen },
  { id: "attainment", label: "Attainment Dashboard", icon: TrendingUp },
  { id: "atr", label: "ATR Tracker", icon: FileText },
  { id: "attainment-settings", label: "Attainment Settings", icon: Settings },
];

const ProgramDetail = ({ programId, onBack, onSelectCourse }) => {
  const [program, setProgram] = useState(null);
  const [activeTab, setActiveTab] = useState("pos");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProgram = async () => {
    try {
      setLoading(true);
      const data = await getProgram(programId);
      setProgram(data.program);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load program");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProgram();
  }, [programId]);

  if (loading) {
    return (
      <div className="p-4 sm:p-8 lg:p-10 pt-16 lg:pt-10 min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          <div className="h-8 w-2/3 mt-3 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          <div className="mt-4 h-1 w-12 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          <div className="mt-10 h-64 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="p-4 sm:p-8 lg:p-10 pt-16 lg:pt-10 min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Back to programs
          </button>
          <div className="p-6 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {error || "Program not found"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 pt-16 lg:pt-10 min-h-screen bg-gray-50 dark:bg-gray-900">
      <motion.div {...motionFadeUp} className="max-w-7xl mx-auto">
        {/* Back link */}
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-accent1 transition-colors mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to programs
        </button>

        {/* Header */}
        <div className="mb-2">
          <div className={microLabelWide}>
            {program.code} · {program.department || "—"}
          </div>
          <h1 className={cn(pageTitle, "text-3xl sm:text-4xl mt-1")}>
            {program.name}
          </h1>
          <div className={cn(accentBarSection, "mt-4")} />
          {program.description && (
            <p className={cn(bodySecondary, "mt-4 max-w-2xl")}>
              {program.description}
            </p>
          )}
        </div>

        {/* Quick badges */}
        <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-sm bg-accent2 text-accent1 font-semibold">
            {program.degreeType}
          </span>
          <span className="px-2.5 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
            {program.durationYears}yr · {program.totalSemesters} sem
          </span>
        </div>

        {/* Tab bar */}
        <div className="mt-10 border-b border-gray-200 dark:border-gray-700">
          <nav className="flex flex-wrap gap-1 -mb-px overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "px-4 py-3 inline-flex items-center gap-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap",
                    isActive
                      ? "border-accent1 text-accent1"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab content */}
        <motion.div key={activeTab} {...motionPageTransition} className="mt-8">
          {activeTab === "pos" && (
            <POsTab programId={programId} program={program} />
          )}
          {activeTab === "psos" && (
            <PSOsTab programId={programId} program={program} />
          )}
          {activeTab === "peos" && (
            <PEOsTab programId={programId} program={program} />
          )}
          {activeTab === "targets" && (
            <TargetsTab programId={programId} program={program} />
          )}
          {activeTab === "courses" && (
            <CoursesTab
              programId={programId}
              program={program}
              onSelectCourse={onSelectCourse}
            />
          )}
          {activeTab === "attainment" && (
            <AttainmentTab programId={programId} />
          )}
          {activeTab === "atr" && <AtrTab programId={programId} />}
          {activeTab === "attainment-settings" && (
            <AttainmentSettingsTab programId={programId} />
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ProgramDetail;
